'use client'

import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import {
  AlertTriangle,
  ArrowRight,
  Camera,
  ChevronDown,
  Eye,
  Images,
  Leaf,
  Loader2,
  PackageCheck,
  ScanSearch,
  Upload,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { GrassTuft } from '@/components/icons/grass-tuft'
import { WeedResultPresentation } from '@/components/weed-identifier/weed-result-presentation'
import { AnalysisLawnChoice } from '@/components/home/analysis-lawn-choice'
import { Button } from '@/components/ui/button'
import { AnonLimitWall, PremiumLimitWall } from '@/components/home/limit-walls'
import { useAuth } from '@/contexts/AuthContext'
import { useAnalyticsUserTier } from '@/hooks/use-analytics-user-tier'
import { useWeedIdCapability } from '@/hooks/use-weed-id-capability'
import { useDefaultLawn } from '@/hooks/use-default-lawn'
import { useAnalysisLawnContext } from '@/hooks/use-analysis-lawn-context'
import { analytics, analysisFunnelEnvelope, getPosthogDistinctId, trackEvent } from '@/lib/analytics'
import { markEmailUnlockHint, resolveAnalyticsUserTier } from '@/lib/analytics/user-tier'
import { ANONYMOUS_WEB_ANALYSIS_LIMITS, REGISTERED_FREE_ANALYSIS_LIMITS } from '@/lib/auth/feature-flags'
import {
  canAccumulateWeedPhotos,
  sortWeedPhotos,
  weedRefinementBaselineUrls,
  weedRefinementGuidance,
  weedRefinementHasNewEvidence,
} from '@/lib/analysis/weed-photo-accumulation'
import { shouldRestoreSignedWeedResult } from '@/lib/analysis/weed-result-restore'
import { claimAnalysisOnce, wasJustAuthed } from '@/lib/claim-analysis'
import { compressImage } from '@/lib/image-compression'
import { isTrustedAnalysisImageUrl } from '@/lib/image-url-validation'
import { errorMessageFrom, safeJson } from '@/lib/safe-json'
import type { WeedIdentificationResult, WeedImageRole } from '@/lib/types/weed-identification'
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES_HOMEPAGE } from '@/lib/upload-constants'

type PhotoSlot = {
  file?: File
  remoteUrl?: string
  preview: string
  role: WeedImageRole
}

const PHOTO_GUIDANCE: Array<{ role: WeedImageRole; title: string; description: string }> = [
  { role: 'overview', title: 'Overview', description: 'Show the whole plant in the lawn.' },
  { role: 'leaf_stem', title: 'Leaf and stem', description: 'Get close enough to see edges and growth pattern.' },
  { role: 'diagnostic_feature', title: 'Flower or seed head', description: 'Optional, but often decisive.' },
]

const FLOW_CONTEXT_KEY = 'weed_identifier_flow_context'
const FUNNEL_ID_KEY = 'weed_identifier_funnel_id'
const LANDED_FUNNEL_KEY = 'weed_identifier_landed_funnel_id'
const HISTORY_FUNNEL_KEY = 'weedIdentifierFunnelId'
const PENDING_WEED_ANALYSIS_KEY = 'pendingWeedAnalysis_limit'
const PENDING_WEED_ANALYSIS_SESSION_KEY = 'pendingWeedAnalysis_limit_session'
const ANALYSIS_RESUME_ID_RE = /^[a-zA-Z0-9_-]{8,120}$/
const WEED_PHOTO_ACCEPT = [
  ...Object.keys(UPLOAD_ACCEPT),
  ...Object.values(UPLOAD_ACCEPT).flat(),
].join(',')

function acceptsSharedPhoto(file: File): boolean {
  if (Object.keys(UPLOAD_ACCEPT).includes(file.type.toLowerCase())) return true
  const name = file.name.toLowerCase()
  return Object.values(UPLOAD_ACCEPT).flat().some((extension) => name.endsWith(extension))
}

function newAnalysisResumeId(): string {
  try {
    return `resume_${crypto.randomUUID().replace(/-/g, '_')}`
  } catch {
    return `resume_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  }
}

type WeedFlowContext = {
  source: string
  campaign?: string
  guide_slug?: string
  experiment_id: string
}

function storedFlowContext(): WeedFlowContext | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.sessionStorage.getItem(FLOW_CONTEXT_KEY)
    return value ? JSON.parse(value) as WeedFlowContext : null
  } catch {
    return null
  }
}

function validFunnelId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,120}$/.test(value)
}

function createFunnelId(): string {
  if (typeof window === 'undefined') return 'weed_server'
  try {
    return crypto.randomUUID().replace(/-/g, '_')
  } catch {
    return `weed_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  }
}

function currentReferrerHost(): string | undefined {
  if (!document.referrer) return undefined
  try {
    return new URL(document.referrer).hostname
  } catch {
    return undefined
  }
}

function eventBase(result: WeedIdentificationResult | null, funnelId: string, analysisId: string | null) {
  const flow = storedFlowContext()
  return {
    funnel_id: funnelId,
    analysis_id: analysisId || undefined,
    weed_id: result?.weedId || undefined,
    platform: 'web',
    result_quality: result?.imageQuality,
    model: result?.model,
    prompt_version: result?.promptVersion,
    taxonomy_version: result?.taxonomyVersion,
    pipeline_version: result?.pipelineVersion,
    source: flow?.source,
    campaign: flow?.campaign,
    guide_slug: flow?.guide_slug,
    experiment_id: flow?.experiment_id || 'weed-id-v1',
    weed_entry: 'dedicated',
  }
}

export function WeedIdentifierExperience() {
  const searchParams = useSearchParams()
  const { user } = useAuth()
  const { lawns } = useDefaultLawn()
  const analysisLawn = useAnalysisLawnContext(lawns, searchParams.get('lawnId'))
  const { capability, isLoading: capabilityLoading } = useWeedIdCapability()
  const isDevProof = process.env.NODE_ENV !== 'production' && searchParams.get('devProof') === '1'
  const [funnelId, setFunnelId] = useState('weed_loading')
  const [photos, setPhotos] = useState<PhotoSlot[]>([])
  const [zipCode, setZipCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<WeedIdentificationResult | null>(null)
  const [analysisId, setAnalysisId] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [restoreDenied, setRestoreDenied] = useState(false)
  const [refinementOfAnalysisId, setRefinementOfAnalysisId] = useState<string | null>(null)
  // Held so the result surface can key its impression events to the same attempt
  // the funnel envelope used. Minted per submit inside the analyze handler.
  const [lastAttemptId, setLastAttemptId] = useState<string | null>(null)
  const [refinementGuidance, setRefinementGuidance] = useState('')
  // Photos the source scan already sent. A refinement must add at least one URL
  // outside this set or resolveRescanExemption refuses the exemption and the
  // follow-up silently spends a Diagnosis credit.
  const [refinementBaselineUrls, setRefinementBaselineUrls] = useState<string[]>([])
  const [supportingPhotosOpen, setSupportingPhotosOpen] = useState(false)
  const [preparingPhotos, setPreparingPhotos] = useState(0)
  const [planLawnId, setPlanLawnId] = useState<string | null | undefined>(undefined)
  const [limitReached, setLimitReached] = useState(false)
  // Anonymous ladder rung echoed by the analyze route's 429 — see the same
  // state in hooks/use-homepage-analysis.ts.
  const [limitTier, setLimitTier] = useState<'anon' | 'email'>('anon')
  const [emailTierObserved, setEmailTierObserved] = useState(false)
  // The ambient tier is module state, so a direct entry to /weed-identifier
  // that never sets it emits a null tier — or, after an in-session visit to the
  // homepage flow, whatever that flow left behind. Same helper, same ladder.
  useAnalyticsUserTier(emailTierObserved)
  const [upgradeRequired, setUpgradeRequired] = useState(false)
  const [resumePending, setResumePending] = useState(searchParams.get('resumeAnalysis') === '1')
  const [analysisResumeId, setAnalysisResumeId] = useState(() => {
    const value = searchParams.get('analysisResumeId')
    return value && ANALYSIS_RESUME_ID_RE.test(value) ? value : null
  })
  const landingTracked = useRef(false)
  const funnelEntryResolved = useRef(false)
  const restoringResultIdRef = useRef<string | null>(null)
  const photosRef = useRef<PhotoSlot[]>([])
  const analysisResumeRef = useRef<string | null>(analysisResumeId)
  const resumeRestoreHandledRef = useRef(false)

  useEffect(() => {
    if (funnelEntryResolved.current) return
    funnelEntryResolved.current = true
    const resumedFunnel = searchParams.get('funnel_id')
    const currentHistoryState = window.history.state && typeof window.history.state === 'object'
      ? window.history.state as Record<string, unknown>
      : {}
    const historyFunnelValue = currentHistoryState[HISTORY_FUNNEL_KEY]
    const historyFunnel = validFunnelId(historyFunnelValue)
      ? historyFunnelValue
      : null
    const sessionFunnelValue = window.sessionStorage.getItem(FUNNEL_ID_KEY)
    const sessionFunnel = validFunnelId(sessionFunnelValue) ? sessionFunnelValue : null
    const continuationRoute = Boolean(
      searchParams.get('analysis_id') ||
      searchParams.get('analysisResumeId') ||
      searchParams.get('resumeAnalysis') === '1' ||
      searchParams.get('refine') === '1',
    )
    const explicitFunnel = validFunnelId(resumedFunnel) ? resumedFunnel : null
    // A funnel belongs to one browser history entry. Hard reloads and Back keep
    // that entry's state; signed-result/refinement/OAuth continuations carry an
    // explicit URL/session marker. A new plain route entry has neither and must
    // start a new journey even though sessionStorage still contains the prior ID.
    const id = explicitFunnel
      ?? historyFunnel
      ?? (continuationRoute ? sessionFunnel : null)
      ?? createFunnelId()
    const isNewJourney = !explicitFunnel && !historyFunnel && !(continuationRoute && sessionFunnel)
    window.sessionStorage.setItem(FUNNEL_ID_KEY, id)
    window.history.replaceState(
      { ...currentHistoryState, [HISTORY_FUNNEL_KEY]: id },
      '',
      window.location.href,
    )
    setFunnelId(id)
    const existingFlow = storedFlowContext()
    const flow = !isNewJourney && existingFlow ? existingFlow : {
      source: searchParams.get('source') || searchParams.get('utm_source') || (document.referrer ? 'referral' : 'direct'),
      campaign: searchParams.get('utm_campaign') || undefined,
      guide_slug: searchParams.get('guide') || undefined,
      experiment_id: 'weed-id-v1',
    }
    if (isNewJourney || !existingFlow) {
      window.sessionStorage.setItem(FLOW_CONTEXT_KEY, JSON.stringify(flow))
    }
    const alreadyTrackedThisFunnel = window.sessionStorage.getItem(LANDED_FUNNEL_KEY) === id
    if (!isDevProof && !landingTracked.current && !alreadyTrackedThisFunnel) {
      landingTracked.current = true
      window.sessionStorage.setItem(LANDED_FUNNEL_KEY, id)
      trackEvent('weed_id_landed', {
        funnel_id: id,
        platform: 'web',
        source: flow.source,
        landing_page: window.location.pathname,
        campaign: flow.campaign,
        guide_slug: flow.guide_slug,
        experiment_id: flow.experiment_id,
        weed_entry: 'dedicated',
      })
    }
  }, [isDevProof, searchParams])

  useEffect(() => {
    const id = searchParams.get('analysis_id')
    const token = searchParams.get('access')
    if (!shouldRestoreSignedWeedResult({
      analysisId: id,
      accessToken: token,
      hasResult: Boolean(result),
      refinementOfAnalysisId,
    })) return
    if (!id) return
    if (restoringResultIdRef.current === id) return
    restoringResultIdRef.current = id
    setBusy(true)
    // The result route authorizes the authenticated owner without a token, so
    // the `access` param rides along only when the URL actually carries one.
    fetch(token
      ? `/api/weed-identifier/result/${id}?access=${encodeURIComponent(token)}`
      : `/api/weed-identifier/result/${id}`)
      .then(async (response) => {
        // A denied or missing result is not an error for this viewer: they are
        // holding a link they cannot read. Drop the inert params so a reload
        // does not retry, and fall through to a fresh scan.
        if (response.status === 403 || response.status === 404) {
          // `restoreDenied`, not the URL alone, is what re-applies the capability
          // gates below: a raw replaceState is not a reliable re-render trigger.
          setRestoreDenied(true)
          restoringResultIdRef.current = null
          const deniedUrl = new URL(window.location.href)
          deniedUrl.searchParams.delete('analysis_id')
          deniedUrl.searchParams.delete('access')
          window.history.replaceState(window.history.state, '', deniedUrl.toString())
          return
        }
        const data = await safeJson<{
          result?: WeedIdentificationResult
          zipCode?: string
          imageUrl?: string
          lawnId?: string | null
          resultAccessToken?: string
        }>(response)
        if (!response.ok || !data?.result) throw new Error('Unable to restore this weed result.')
        setAnalysisId(id)
        // Tokenless owner restores receive their capability in the response body.
        const restoredToken = token || data.resultAccessToken || null
        setAccessToken(restoredToken)
        setZipCode(data.zipCode || '')
        setPlanLawnId(data.lawnId === undefined ? undefined : data.lawnId)
        const refineImmediately = searchParams.get('refine') === '1'
        const overviewUrl = data.result.image || data.imageUrl
        // `refine=1` only accumulates when another view can move the outcome.
        // A catalog gap arriving here still renders its terminal result.
        if (refineImmediately && overviewUrl && canAccumulateWeedPhotos(data.result)) {
          const baselineUrls = weedRefinementBaselineUrls(data.result)
          setRefinementOfAnalysisId(id)
          setRefinementGuidance(weedRefinementGuidance(data.result))
          setRefinementBaselineUrls(baselineUrls.length > 0 ? baselineUrls : [overviewUrl])
          setSupportingPhotosOpen(true)
          setPhotos([{ role: 'overview', preview: overviewUrl, remoteUrl: overviewUrl }])
          // This effect's closure predates the funnel-entry effect's setState,
          // so read the resolved ID from the session it already wrote.
          const storedFunnelId = window.sessionStorage.getItem(FUNNEL_ID_KEY)
          const resolvedFunnelId = validFunnelId(storedFunnelId) ? storedFunnelId : funnelId
          trackEvent('weed_id_refinement_started', {
            ...eventBase(data.result, resolvedFunnelId, id),
            ...analysisFunnelEnvelope({
              funnelId: resolvedFunnelId,
              analysisId: id,
              mode: 'weed',
              source: 'weed_result_refinement_link',
              imageRoles: ['primary', 'supporting_requested'],
              rawConfidence: data.result.confidence,
              displayConfidence: data.result.displayConfidence,
              gatingReasons: [data.result.abstentionReason, ...(data.result.missingEvidence ?? [])]
                .filter((reason): reason is string => Boolean(reason)),
              safetyProof: 'additional_evidence_required',
              model: data.result.model ?? null,
              promptVersion: data.result.promptVersion ?? null,
              promptStrategy: data.result.promptStrategy ?? null,
              pipelineVersion: data.result.pipelineVersion ?? null,
              authState: user ? 'authenticated' : 'anonymous',
              actualDestination: 'supporting_photo_capture',
            }),
            reason: data.result.abstentionReason ?? 'inconclusive',
            preserves_primary_photo: true,
          })
          const cleanUrl = new URL(window.location.href)
          cleanUrl.searchParams.delete('refine')
          window.history.replaceState(window.history.state, '', cleanUrl.toString())
        } else {
          // A restored result is not this mount's attempt. `lastAttemptId`
          // still holds whatever the last in-session scan minted, and the
          // presentation keys its impression and every next-action on it, so
          // leaving it would stamp result B's events with attempt A. The
          // original attempt is not recoverable client-side; clearing it lets
          // the presentation fall back to `analysisId`, which is the correct
          // stable join key for a result that was not scanned here.
          setLastAttemptId(null)
          setResult(data.result)
        }
        if (wasJustAuthed()) {
          claimAnalysisOnce(id, 'weed_identifier_oauth_resume', {
            resultAccessToken: restoredToken ?? undefined,
            analyticsContext: {
              analysisId: id,
              mode: 'weed',
              planType: 'control',
              outcome: data.result.outcome,
              entryPath: 'weed_identifier_oauth_return',
              funnelId,
              subject: data.result.weedName || undefined,
            },
          })
        }
      })
      .catch((restoreError) => setError(errorMessageFrom(restoreError, 'Unable to restore this weed result.')))
      .finally(() => setBusy(false))
  }, [funnelId, refinementOfAnalysisId, result, searchParams, user])

  useEffect(() => {
    photosRef.current = photos
  }, [photos])

  // Restore the already-uploaded weed photos after OAuth. Files cannot survive
  // navigation, so the quota response stores the server URLs + semantic roles
  // and this effect rebuilds the exact request before auto-resume.
  useEffect(() => {
    if (!user || result || resumeRestoreHandledRef.current) return
    resumeRestoreHandledRef.current = true
    try {
      let raw: string | null = null
      let restoreSource: 'local_storage' | 'session_storage' | 'first_party_redirect' = 'local_storage'
      try {
        raw = window.localStorage.getItem(PENDING_WEED_ANALYSIS_KEY)
      } catch { /* localStorage can be unavailable in private browsing */ }
      if (!raw) {
        try {
          raw = window.sessionStorage.getItem(PENDING_WEED_ANALYSIS_SESSION_KEY)
          if (raw) restoreSource = 'session_storage'
        } catch { /* sessionStorage can also be unavailable */ }
      }
      if (!raw) {
        // AuthContext keeps the complete destination first-party while only
        // sending the compact tool + resume id fallback to the OAuth provider.
        // Rebuild the request from that recovered URL if browser storage was
        // cleaned during the provider round trip.
        const overviewUrl = searchParams.get('imageUrl')
        if (overviewUrl && isTrustedAnalysisImageUrl(overviewUrl)) {
          const supportingUrls = searchParams.getAll('supportingImageUrl')
          const supportingRoles = searchParams.getAll('supportingImageRole')
          const redirectPhotos: Array<{ remoteUrl: string; role: WeedImageRole }> = [
            { remoteUrl: overviewUrl, role: 'overview' },
          ]
          supportingUrls.forEach((remoteUrl, index) => {
            const role = supportingRoles[index]
            if (
              isTrustedAnalysisImageUrl(remoteUrl) &&
              (role === 'leaf_stem' || role === 'diagnostic_feature')
            ) {
              redirectPhotos.push({ remoteUrl, role })
            }
          })
          raw = JSON.stringify({
            photos: redirectPhotos,
            zipCode: searchParams.get('zipCode') || '',
            funnelId,
            analysisResumeId: analysisResumeId || searchParams.get('analysisResumeId'),
            savedAt: Date.now(),
          })
          restoreSource = 'first_party_redirect'
        }
      }
      if (!raw) {
        if (searchParams.get('resumeAnalysis') === '1') {
          setError('You are signed in, but this browser could not restore the uploaded photo. Add it again to continue your weed ID here.')
          trackEvent('analysis_resume_restore_failed', {
            analysis_resume_id: analysisResumeId || undefined,
            mode: 'weed',
            entry_path: '/weed-identifier',
            reason: 'missing_browser_state',
          })
        }
        return
      }
      const pending = JSON.parse(raw) as {
        photos?: Array<{ remoteUrl?: unknown; role?: unknown }>
        zipCode?: unknown
        funnelId?: unknown
        refinementOfAnalysisId?: unknown
        refinementGuidance?: unknown
        refinementBaselineUrls?: unknown
        accessToken?: unknown
        analysisResumeId?: unknown
        savedAt?: unknown
      }
      try {
        window.localStorage.removeItem(PENDING_WEED_ANALYSIS_KEY)
      } catch { /* storage cleanup is best-effort */ }
      try {
        window.sessionStorage.removeItem(PENDING_WEED_ANALYSIS_SESSION_KEY)
      } catch { /* storage cleanup is best-effort */ }

      const ONE_HOUR = 60 * 60 * 1000
      if (
        typeof pending.savedAt !== 'number' ||
        Date.now() - pending.savedAt > ONE_HOUR ||
        typeof pending.analysisResumeId !== 'string' ||
        !ANALYSIS_RESUME_ID_RE.test(pending.analysisResumeId) ||
        !Array.isArray(pending.photos)
      ) return

      const restoredPhotos = pending.photos.flatMap((photo): PhotoSlot[] => {
        if (
          typeof photo.remoteUrl !== 'string' ||
          !['overview', 'leaf_stem', 'diagnostic_feature'].includes(String(photo.role))
        ) return []
        return [{
          remoteUrl: photo.remoteUrl,
          preview: photo.remoteUrl,
          role: photo.role as WeedImageRole,
        }]
      })
      if (!restoredPhotos.some((photo) => photo.role === 'overview')) return

      setPhotos(restoredPhotos)
      if (typeof pending.zipCode === 'string') setZipCode(pending.zipCode)
      if (typeof pending.funnelId === 'string' && /^[a-zA-Z0-9_-]{8,100}$/.test(pending.funnelId)) {
        window.sessionStorage.setItem('weed_identifier_funnel_id', pending.funnelId)
        setFunnelId(pending.funnelId)
      }
      if (typeof pending.refinementOfAnalysisId === 'string') {
        setRefinementOfAnalysisId(pending.refinementOfAnalysisId)
      }
      if (typeof pending.refinementGuidance === 'string') {
        setRefinementGuidance(pending.refinementGuidance)
      }
      if (Array.isArray(pending.refinementBaselineUrls)) {
        setRefinementBaselineUrls(
          pending.refinementBaselineUrls.filter(
            (url): url is string => typeof url === 'string' && url.length > 0,
          ),
        )
      }
      if (typeof pending.accessToken === 'string') setAccessToken(pending.accessToken)
      analysisResumeRef.current = pending.analysisResumeId
      setAnalysisResumeId(pending.analysisResumeId)
      setResumePending(true)
      trackEvent('analysis_resume_restored', {
        analysis_resume_id: pending.analysisResumeId,
        mode: 'weed',
        entry_path: '/weed-identifier',
        restore_source: restoreSource,
        image_count: restoredPhotos.length,
      })

      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete('resumeAnalysis')
      cleanUrl.searchParams.delete('analysisResumeId')
      cleanUrl.searchParams.delete('mode')
      cleanUrl.searchParams.delete('resume')
      cleanUrl.searchParams.delete('imageUrl')
      cleanUrl.searchParams.delete('supportingImageUrl')
      cleanUrl.searchParams.delete('supportingImageRole')
      cleanUrl.searchParams.delete('zipCode')
      window.history.replaceState(window.history.state, '', cleanUrl.toString())
    } catch {
      // Storage can be unavailable in private browsing. The callback fallback
      // still returns to /weed-identifier so the user can continue there.
    }
  }, [analysisResumeId, funnelId, result, searchParams, user])

  useEffect(() => () => {
    photosRef.current.forEach((photo) => {
      if (photo.file && photo.preview.startsWith('blob:')) URL.revokeObjectURL(photo.preview)
    })
  }, [])

  const addPhoto = async (file: File, role: WeedImageRole) => {
    setError('')
    if (!acceptsSharedPhoto(file)) {
      setError('Use a JPG, PNG, WebP, HEIC, or HEIF photo.')
      return
    }
    if (file.size > UPLOAD_MAX_BYTES_HOMEPAGE) {
      setError(`Each original photo must be under ${Math.round(UPLOAD_MAX_BYTES_HOMEPAGE / (1024 * 1024))} MB.`)
      return
    }
    setPreparingPhotos((count) => count + 1)
    try {
      const prepared = await compressImage(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1920,
        quality: 0.85,
        minQuality: 0.5,
        maxMegapixels: 50,
      })
      setPhotos((current) => {
        const existing = current.find((photo) => photo.role === role)
        if (existing?.file && existing.preview.startsWith('blob:')) URL.revokeObjectURL(existing.preview)
        return sortWeedPhotos([...current.filter((photo) => photo.role !== role), {
          file: prepared.file,
          role,
          preview: URL.createObjectURL(prepared.file),
        }])
      })
    } catch (photoError) {
      setError(errorMessageFrom(photoError, 'We could not prepare this photo. Try a different image.'))
    } finally {
      setPreparingPhotos((count) => Math.max(0, count - 1))
    }
  }

  const analyze = async () => {
    if (!photos.some((photo) => photo.role === 'overview')) return setError('Add an overview photo first.')
    if (
      refinementOfAnalysisId &&
      !weedRefinementHasNewEvidence({ photos, baselineUrls: refinementBaselineUrls })
    ) {
      return setError(refinementGuidance || 'Add the requested close-up or diagnostic photo before refining this result.')
    }
    if (preparingPhotos > 0) return setError('Wait for the selected photo to finish preparing.')
    if (analysisLawn.choiceRequired && !analysisLawn.choiceMade) {
      return setError('Choose which lawn this is, or choose Just scan.')
    }
    if (!/^\d{5}$/.test(zipCode)) return setError('Enter a valid 5-digit ZIP code.')
    setBusy(true)
    setError('')
    const attemptId = crypto.randomUUID()
    setLastAttemptId(attemptId)
    const funnelEnvelope = (
      envelopeResult: WeedIdentificationResult | null,
      envelopeAnalysisId: string | null,
      actualDestination: string,
    ) => analysisFunnelEnvelope({
      funnelId,
      attemptId,
      analysisId: envelopeAnalysisId,
      originLawnId: searchParams.get('lawnId'),
      selectedLawnId: analysisLawn.lawnContext?.lawnId ?? null,
      zipMatch: analysisLawn.selectedLawn?.zip_code
        ? analysisLawn.selectedLawn.zip_code === zipCode
        : null,
      mode: 'weed',
      source: storedFlowContext()?.source ?? 'dedicated',
      imageRoles: photos.map((photo) => photo.role),
      rawConfidence: envelopeResult?.confidence,
      displayConfidence: envelopeResult?.displayConfidence,
      gatingReasons: envelopeResult
        ? [
            envelopeResult.outcome === 'supported' ? null : envelopeResult.outcome,
            envelopeResult.abstentionReason,
            ...envelopeResult.missingEvidence,
          ].filter((reason): reason is string => Boolean(reason))
        : null,
      safetyProof: envelopeResult
        ? envelopeResult.hostGrassVerified
          ? 'host_grass_verified'
          : 'host_grass_unverified'
        : null,
      model: envelopeResult?.model,
      promptVersion: envelopeResult?.promptVersion,
      promptStrategy: envelopeResult?.promptStrategy,
      pipelineVersion: envelopeResult?.pipelineVersion,
      authState: user ? 'authenticated' : 'anonymous',
      actualDestination,
    })
    let failureStage: 'upload' | 'analysis' = 'upload'
    trackEvent('weed_id_scan_started', {
      ...eventBase(null, funnelId, null),
      ...funnelEnvelope(null, null, 'analysis_pipeline'),
      image_count: photos.length,
      refinement_of_analysis_id: refinementOfAnalysisId || undefined,
    })
    try {
      const imageUrls: string[] = []
      const uploadedPhotos: PhotoSlot[] = []
      for (const photo of photos) {
        if (photo.remoteUrl) {
          imageUrls.push(photo.remoteUrl)
          uploadedPhotos.push({ ...photo, preview: photo.remoteUrl })
          continue
        }
        if (!photo.file) throw new Error('A selected photo is no longer available. Add it again.')
        const form = new FormData()
        form.append('file', photo.file)
        form.append('category', 'weed-identification')
        form.append('attemptId', attemptId)
        const uploadResponse = await fetch('/api/upload/image', { method: 'POST', body: form })
        const uploaded = await safeJson<{ url?: string; error?: unknown }>(uploadResponse)
        if (!uploadResponse.ok || !uploaded?.url) throw new Error(errorMessageFrom(uploaded?.error, 'Photo upload failed.'))
        imageUrls.push(uploaded.url)
        uploadedPhotos.push({ role: photo.role, remoteUrl: uploaded.url, preview: uploaded.url })
      }
      setPhotos(uploadedPhotos)
      // From this point forward, failures belong to inference rather than upload.
      failureStage = 'analysis'
      const response = await fetch('/api/ai/analyze-grass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'weed',
          imageUrl: imageUrls[0],
          imageUrls: imageUrls.slice(1),
          imageRoles: photos.map((photo) => photo.role),
          zipCode,
          attemptId,
          funnelId,
          distinctId: getPosthogDistinctId(),
          ...(analysisLawn.lawnContext?.lawnId
            ? {
                lawnId: analysisLawn.lawnContext.lawnId,
                knownGrassType: analysisLawn.lawnContext.knownGrassType,
                grassTypeProvenance: analysisLawn.lawnContext.grassTypeProvenance,
                lawnContextIntent: analysisLawn.lawnContext.lawnContextIntent,
              }
            : {}),
          rescanOfAnalysisId: refinementOfAnalysisId || undefined,
          sourceResultAccessToken: refinementOfAnalysisId ? accessToken || undefined : undefined,
          attribution: {
            firstTouchSource: storedFlowContext()?.source || searchParams.get('source') || searchParams.get('utm_source') || (document.referrer ? 'referral' : 'direct'),
            landingRoute: window.location.pathname,
            referrerHost: currentReferrerHost(),
            utmSource: searchParams.get('utm_source') || undefined,
            utmMedium: searchParams.get('utm_medium') || undefined,
            utmCampaign: searchParams.get('utm_campaign') || undefined,
          },
        }),
      })
      const data = await safeJson<{
        result?: WeedIdentificationResult
        analysisId?: string
        resultAccessToken?: string
        signupRequired?: boolean
        tier?: 'anon' | 'email'
        upgradeRequired?: boolean
        error?: unknown
      }>(response)
      if (!response.ok && data?.signupRequired) {
        // Missing `tier` (older deploy) reads as the no-account rung so the
        // email lane still renders.
        setLimitTier(data.tier === 'email' ? 'email' : 'anon')
        if (data.tier === 'email') {
          markEmailUnlockHint()
          setEmailTierObserved(true)
        }
        const resumeId = newAnalysisResumeId()
        analysisResumeRef.current = resumeId
        setAnalysisResumeId(resumeId)
        const pendingWeedAnalysis = JSON.stringify({
          photos: uploadedPhotos.map((photo) => ({ remoteUrl: photo.remoteUrl, role: photo.role })),
          zipCode,
          funnelId,
          refinementOfAnalysisId,
          refinementGuidance,
          refinementBaselineUrls,
          accessToken,
          analysisResumeId: resumeId,
          savedAt: Date.now(),
        })
        try {
          window.localStorage.setItem(PENDING_WEED_ANALYSIS_KEY, pendingWeedAnalysis)
        } catch { /* same-path callback remains available */ }
        // OAuth leaves the origin in the same tab. Mirror the compact pending
        // request in sessionStorage so privacy tools or storage cleanup that
        // drops localStorage cannot break the promised no-re-upload return.
        try {
          window.sessionStorage.setItem(PENDING_WEED_ANALYSIS_SESSION_KEY, pendingWeedAnalysis)
        } catch { /* same-path callback remains available */ }
        analytics.analysisLimitReached({
          mode: 'weed',
          reason: data?.tier === 'email'
            ? 'email_tier_quota_exhausted'
            : 'anon_quota_exhausted',
          surface: 'weed_identifier:analysis_limit_wall',
          analysisResumeId: resumeId,
          entryPath: '/weed-identifier',
          imageCount: uploadedPhotos.length,
          funnelId,
          attemptId,
          // setEmailTierObserved only reaches the ambient rung on the next
          // effect pass, so this event would otherwise say
          // `email_tier_quota_exhausted` while `user_tier` still said
          // `anonymous`. Resolve the rung the response itself just proved.
          // `signupRequired` is the signed-out lane by definition; an older
          // deploy that omits `tier` proves nothing, so it keeps the ambient
          // value rather than asserting a rung.
          userTier: data?.tier
            ? resolveAnalyticsUserTier({
                isAuthenticated: false,
                emailUnlocked: data.tier === 'email',
              })
            : undefined,
        })
        setLimitReached(true)
        return
      }
      if (!response.ok && data?.upgradeRequired) {
        if (analysisResumeRef.current) {
          trackEvent('analysis_resume_blocked', {
            analysis_resume_id: analysisResumeRef.current,
            mode: 'weed',
            entry_path: '/weed-identifier',
            reason: 'registered_quota_exhausted',
          })
          analysisResumeRef.current = null
        }
        trackEvent('analysis_upgrade_required', { mode: 'weed' })
        setUpgradeRequired(true)
        return
      }
      if (!response.ok || !data?.result || !data.analysisId || !data.resultAccessToken) {
        throw new Error(errorMessageFrom(data?.error, 'We could not complete this weed ID.'))
      }
      if (analysisResumeRef.current) {
        trackEvent('analysis_resume_completed', {
          analysis_resume_id: analysisResumeRef.current,
          mode: 'weed',
          entry_path: '/weed-identifier',
          outcome: 'success',
        })
        analysisResumeRef.current = null
        setAnalysisResumeId(null)
      }
      setResult(data.result)
      setAnalysisId(data.analysisId)
      setAccessToken(data.resultAccessToken)
      setPlanLawnId(analysisLawn.lawnContext?.lawnId ?? null)
      setRefinementOfAnalysisId(null)
      setRefinementGuidance('')
      setRefinementBaselineUrls([])
      window.history.replaceState(
        window.history.state,
        '',
        `/weed-identifier?analysis_id=${data.analysisId}&access=${encodeURIComponent(data.resultAccessToken)}&funnel_id=${encodeURIComponent(funnelId)}`,
      )
      trackEvent('weed_id_result_rendered', {
        ...eventBase(data.result, funnelId, data.analysisId),
        ...funnelEnvelope(
          data.result,
          data.analysisId,
          `/weed-identifier?analysis_id=${encodeURIComponent(data.analysisId)}`,
        ),
        outcome: data.result.outcome,
        result_confidence: data.result.displayConfidence,
        candidate_margin: data.result.candidateMargin ?? undefined,
      })
    } catch (analysisError) {
      trackEvent('weed_id_scan_failed', {
        ...eventBase(null, funnelId, null),
        ...funnelEnvelope(null, null, 'weed_scan_error'),
        failure_stage: failureStage,
        failure_reason: analysisError instanceof TypeError ? 'network' : 'request_failed',
        refinement_of_analysis_id: refinementOfAnalysisId || undefined,
      })
      if (analysisResumeRef.current) {
        trackEvent('analysis_resume_failed', {
          analysis_resume_id: analysisResumeRef.current,
          mode: 'weed',
          entry_path: '/weed-identifier',
          reason: analysisError instanceof TypeError ? 'network' : 'request_failed',
          failure_stage: failureStage,
        })
        analysisResumeRef.current = null
      }
      setError(errorMessageFrom(analysisError, 'We could not complete this weed ID.'))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (
      !user ||
      !resumePending ||
      !analysisResumeRef.current ||
      busy ||
      result ||
      !/^\d{5}$/.test(zipCode) ||
      !photos.some((photo) => photo.role === 'overview' && photo.remoteUrl)
    ) return

    setResumePending(false)
    setLimitReached(false)
    setUpgradeRequired(false)
    trackEvent('analysis_resume_started', {
      analysis_resume_id: analysisResumeRef.current,
      mode: 'weed',
      entry_path: '/weed-identifier',
      trigger: 'oauth_return',
    })
    void analyze()
  // `analyze` intentionally reads the fully restored render's state. The
  // pending bit is cleared before dispatch, so later renders cannot re-run it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, resumePending, busy, result, zipCode, photos])

  const weedOAuthResumeUrl = (() => {
    if (!analysisResumeId) return '/weed-identifier'

    const params = new URLSearchParams({
      resumeAnalysis: '1',
      analysisResumeId,
      mode: 'weed',
    })
    const overview = photos.find((photo) => photo.role === 'overview' && isTrustedAnalysisImageUrl(photo.remoteUrl))
    if (overview?.remoteUrl) {
      params.set('resume', '1')
      params.set('imageUrl', overview.remoteUrl)
      if (zipCode) params.set('zipCode', zipCode)
      photos
        .filter((photo) => photo.role !== 'overview' && isTrustedAnalysisImageUrl(photo.remoteUrl))
        .forEach((photo) => {
          params.append('supportingImageUrl', photo.remoteUrl!)
          params.append('supportingImageRole', photo.role)
        })
    }
    return `/weed-identifier?${params.toString()}`
  })()

  const restoringSignedResult = !restoreDenied && shouldRestoreSignedWeedResult({
    analysisId: searchParams.get('analysis_id'),
    accessToken: searchParams.get('access'),
    hasResult: Boolean(result),
    refinementOfAnalysisId,
  })
  if (capabilityLoading && !isDevProof && !result && !restoringSignedResult) {
    return <div className="min-h-screen bg-gray-50/70" aria-busy="true" />
  }
  if (!capabilityLoading && !capability.analysisEnabled && !isDevProof && !result && !restoringSignedResult) {
    return <div className="mx-auto max-w-2xl px-4 py-24 text-center"><Leaf className="mx-auto h-10 w-10 text-emerald-700" /><h1 className="mt-4 text-3xl font-bold">Weed ID is in expert review</h1><p className="mt-3 text-slate-600">We are validating the first release before opening it more broadly.</p></div>
  }
  if (limitReached || upgradeRequired) {
    const extraWeedIds = Math.max(
      0,
      REGISTERED_FREE_ANALYSIS_LIMITS.weed - ANONYMOUS_WEB_ANALYSIS_LIMITS.weed,
    )
    return (
      <div className="min-h-screen bg-gray-50/70 px-4 py-16 text-gray-950">
        {/* The anon limit wall owns its full frame (edge-bleed photo header),
            so this card drops its padding and clips the corners while the
            wall shows; the premium wall keeps the padded layout. */}
        <div className={`mx-auto max-w-lg rounded-3xl border border-green-100 bg-white text-center shadow-[0_1px_2px_rgba(16,24,16,0.04),0_24px_64px_-32px_rgba(22,101,52,0.16)] ${limitReached ? "overflow-hidden" : "p-6 sm:p-8"}`}>
          {limitReached ? (
            <AnonLimitWall
              mode="weed"
              imageSrc={photos[0]?.preview || null}
              oauthRedirectTo={weedOAuthResumeUrl}
              extraScans={extraWeedIds}
              appLinkPlacement="weed_id_limit_anon"
              zipCode={zipCode}
              tier={limitTier}
            />
          ) : (
            <PremiumLimitWall
              mode="weed"
              imageSrc={photos[0]?.preview || null}
              pricingHref="/pricing?source=weed_id_limit"
              appLinkPlacement="weed_id_limit_upgrade"
            />
          )}
        </div>
      </div>
    )
  }
  if (result && analysisId && accessToken) {
    return (
      <div className="min-h-screen bg-gray-50/70 text-gray-950">
        <WeedResultPresentation
          result={result}
          analysisId={analysisId}
          accessToken={accessToken}
          funnelId={funnelId}
          attemptId={lastAttemptId ?? undefined}
          entry="dedicated"
          planLawnId={planLawnId}
          zipCode={zipCode}
          onRefine={() => {
            const baselineUrls = weedRefinementBaselineUrls(result)
            setRefinementOfAnalysisId(analysisId)
            setRefinementGuidance(weedRefinementGuidance(result))
            setRefinementBaselineUrls(baselineUrls)
            setSupportingPhotosOpen(true)
            // Every photo already sent is kept. The next scan reads the whole
            // accumulated set, so the user only supplies the missing view.
            setPhotos((current) => sortWeedPhotos(
              current.some((photo) => photo.role === 'overview') || !result.image
                ? current
                : [{ role: 'overview', preview: result.image, remoteUrl: result.image }],
            ))
            setResult(null)
            trackEvent('weed_id_refinement_started', {
              ...eventBase(result, funnelId, analysisId),
              ...analysisFunnelEnvelope({
                funnelId,
                analysisId,
                // Same attempt the result impression and its next-action
                // events carry, or the refinement cannot be joined to them.
                attemptId: lastAttemptId ?? undefined,
                originLawnId: searchParams.get('lawnId'),
                selectedLawnId: analysisLawn.lawnContext?.lawnId ?? null,
                mode: 'weed',
                source: 'weed_result_refinement',
                imageRoles: ['primary', 'supporting_requested'],
                rawConfidence: result.confidence,
                displayConfidence: result.displayConfidence,
                gatingReasons: [result.abstentionReason, ...(result.missingEvidence ?? [])]
                  .filter((reason): reason is string => Boolean(reason)),
                safetyProof: 'additional_evidence_required',
                model: result.model ?? null,
                promptVersion: result.promptVersion ?? null,
                promptStrategy: result.promptStrategy ?? null,
                pipelineVersion: result.pipelineVersion ?? null,
                authState: user ? 'authenticated' : 'anonymous',
                actualDestination: 'supporting_photo_capture',
              }),
              reason: result.abstentionReason ?? 'inconclusive',
              preserves_primary_photo: true,
              image_count: photos.length || (result.imageUrls?.length ?? 1),
            })
          }}
          onRetake={() => {
            photos.forEach((photo) => {
              if (photo.file && photo.preview.startsWith('blob:')) URL.revokeObjectURL(photo.preview)
            })
            setPhotos([])
            setResult(null)
            setAnalysisId(null)
            setAccessToken(null)
            // A retake has no attempt until `analyze` mints one. Keeping the
            // spent id would attach the next scan's pre-result events to the
            // previous attempt.
            setLastAttemptId(null)
            setRefinementOfAnalysisId(null)
            setRefinementGuidance('')
            setRefinementBaselineUrls([])
            setSupportingPhotosOpen(false)
            setError('')
            restoringResultIdRef.current = null
            window.history.replaceState(
              window.history.state,
              '',
              `/weed-identifier?funnel_id=${encodeURIComponent(funnelId)}`,
            )
          }}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50/70 text-gray-950">
      <section className="border-b border-green-100 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 md:py-16 lg:grid-cols-[1.08fr_.92fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-green-100 bg-green-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700"><ScanSearch className="h-4 w-4" /> Evidence-based lawn weed ID</div>
            <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-[1.02] tracking-[-0.035em] text-gray-900 sm:text-5xl lg:text-6xl">Name the weed. See the evidence. Get the next steps.</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">Upload a photo to get the likely weed, the visible clues, and curated control, removal, and prevention recommendations.</p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-gray-600"><span className="inline-flex items-center gap-2"><Eye className="h-4 w-4 text-green-700" /> Answer before signup</span><span className="inline-flex items-center gap-2"><PackageCheck className="h-4 w-4 text-green-700" /> Curated product picks</span><span className="inline-flex items-center gap-2"><GrassTuft className="h-4 w-4 text-green-700" /> Honest inconclusive result</span></div>
          </div>
          <div className="rounded-3xl border border-green-100 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,16,0.04),0_24px_64px_-32px_rgba(22,101,52,0.16)] sm:p-7">
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">{refinementOfAnalysisId ? 'Add the requested evidence' : 'Start with the plant in place'}</p>
            <p className="mt-1 text-sm text-gray-600">{refinementOfAnalysisId ? (refinementGuidance || 'We keep your first photo and read every view together.') : 'One clear photo is enough to start.'}</p>

            {(() => {
              const item = PHOTO_GUIDANCE[0]
              const photo = photos.find((candidate) => candidate.role === item.role)
              return (
                <label className={`relative mt-4 flex min-h-44 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed p-5 text-center transition focus-within:ring-2 focus-within:ring-green-600 focus-within:ring-offset-2 hover:border-green-600 ${photo ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-gray-50/70'}`}>
                  <input type="file" accept={WEED_PHOTO_ACCEPT} capture="environment" className="sr-only ph-no-capture" data-ph-no-capture="true" onChange={(event) => { const file = event.target.files?.[0]; if (file) void addPhoto(file, item.role); event.currentTarget.value = '' }} />
                  {photo ? <Image src={photo.preview} alt="Selected weed photo" fill sizes="(min-width: 1024px) 420px, 100vw" className="object-cover" unoptimized /> : <><span className="flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-green-700"><Camera className="h-5 w-5" /></span><span className="mt-4 text-sm font-extrabold text-gray-900">Add the weed photo</span><span className="mt-1 text-xs leading-5 text-gray-500">Show the whole plant where it is growing.</span></>}
                  {photo ? <span className="absolute bottom-3 left-3 rounded-full bg-slate-950/75 px-2.5 py-1 text-[10px] font-bold text-white">Overview photo</span> : null}
                </label>
              )
            })()}

            <button
              type="button"
              aria-expanded={supportingPhotosOpen}
              aria-controls="weed-supporting-photos"
              onClick={() => setSupportingPhotosOpen((open) => !open)}
              className="mt-3 flex min-h-11 w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 text-left text-sm font-bold text-gray-800 transition hover:border-green-300 hover:bg-green-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
            >
              <Images className="h-4 w-4 shrink-0 text-green-700" />
              <span className="flex-1">Add supporting photos{refinementOfAnalysisId ? null : <span className="font-normal text-gray-500"> · optional</span>}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${supportingPhotosOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {supportingPhotosOpen ? (
              <div id="weed-supporting-photos" className="mt-3 grid grid-cols-2 gap-2">
                {PHOTO_GUIDANCE.slice(1).map((item) => {
                  const photo = photos.find((candidate) => candidate.role === item.role)
                  return <label key={item.role} className={`relative flex min-h-32 cursor-pointer flex-col overflow-hidden rounded-2xl border border-dashed p-3 transition focus-within:ring-2 focus-within:ring-green-600 focus-within:ring-offset-2 hover:border-green-600 ${photo ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-gray-50/70'}`}>
                    <input type="file" accept={WEED_PHOTO_ACCEPT} className="sr-only ph-no-capture" data-ph-no-capture="true" onChange={(event) => { const file = event.target.files?.[0]; if (file) void addPhoto(file, item.role); event.currentTarget.value = '' }} />
                    {photo ? <Image src={photo.preview} alt="Selected supporting weed photo" fill sizes="200px" className="object-cover" unoptimized /> : <><span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700"><Upload className="h-4 w-4" /></span><span className="mt-auto text-xs font-extrabold text-gray-900">{item.title}</span><span className="mt-1 hidden text-[11px] leading-4 text-gray-500 sm:block">{item.description}</span></>}
                    {photo ? <span className="absolute bottom-2 left-2 rounded-full bg-slate-950/75 px-2 py-1 text-[10px] font-bold text-white">{item.title}</span> : null}
                  </label>
                })}
              </div>
            ) : null}
            <label className="mt-5 block text-sm font-bold text-gray-800">ZIP code <span className="font-normal text-gray-500">for regional context</span><input value={zipCode} onChange={(event) => setZipCode(event.target.value.replace(/\D/g, '').slice(0, 5))} inputMode="numeric" autoComplete="postal-code" placeholder="e.g. 27601" className="mt-2 h-12 w-full rounded-xl border border-gray-300 bg-white px-4 text-base outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/15" /></label>
            {analysisLawn.choiceRequired ? (
              <div className="mt-5">
                <AnalysisLawnChoice
                  lawns={lawns}
                  choice={analysisLawn.choice}
                  onChange={analysisLawn.selectLawn}
                  mode="weed"
                />
              </div>
            ) : null}
            {error ? <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p> : null}
            <Button disabled={busy || preparingPhotos > 0 || (analysisLawn.choiceRequired && !analysisLawn.choiceMade)} onClick={analyze} className="mt-5 h-12 w-full rounded-xl bg-green-600 text-base font-extrabold hover:bg-green-700">{busy || preparingPhotos > 0 ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{preparingPhotos > 0 ? 'Preparing photo...' : 'Analyzing visible field marks...'}</> : <>{refinementOfAnalysisId ? 'Refine this weed ID' : 'Identify this weed'} <ArrowRight className="ml-2 h-4 w-4" /></>}</Button>
            <p className="mt-3 text-center text-xs text-gray-500">One weed ID uses your shared Diagnosis allowance. An explicit supporting-photo refinement is free.</p>
          </div>
        </div>
      </section>
    </div>
  )
}
